import { WebLayout } from 'components/Layout';
import React, { useEffect, useState } from 'react';
import { Motion, spring } from 'react-motion';
import { Mail, Phone, MapPin } from 'lucide-react';
import { Button } from 'components/Elements';
import { InputField, RegularTextAreaField, Form } from 'components/Form';
import { useMutation } from 'react-query';
import { contactUs } from '../api/contactUs';
import Lottie from 'lottie-react';
import checkAnimation from 'assets/check.json';

export const ContactUs = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const contactMutation = useMutation(contactUs, {
    onSuccess: () => {
      setIsSubmitted(true);
    },
    onError: (error) => {
      console.error('Error sending message:', error);
    },
  });

  return (
    <WebLayout>
      {/* Header Section */}
      <Motion
        defaultStyle={{ opacity: 0, translateY: -20 }}
        style={{
          opacity: spring(1, { stiffness: 60, damping: 15 }),
          translateY: spring(0, { stiffness: 60, damping: 15 }),
        }}
      >
        {(style) => (
          <div
            className="bg-[#26C4FF] p-6 md:p-12 mt-8 mb-8 md:px-40 text-white text-center"
            style={{ opacity: style.opacity, transform: `translateY(${style.translateY}px)` }}
          >
            <h1 className="text-4xl md:text-7xl font-black mb-4">Contact Us</h1>
            <p className="text-lg md:text-xl">Have any questions? Reach out to us anytime.</p>
          </div>
        )}
      </Motion>

      {/* Contact Information Section */}
      <Motion
        defaultStyle={{ opacity: 0 }}
        style={{ opacity: spring(1, { stiffness: 60, damping: 15 }) }}
      >
        {(style) => (
          <div className="max-w-4xl mx-auto px-4 sm:px-8 mb-8" style={{ opacity: style.opacity }}>
            <div className="flex flex-col md:flex-row justify-around items-center gap-6">
              <div className="flex items-center gap-2">
                <Mail className="w-6 h-6 text-blue-600" />
                <span className="text-gray-700">support@wire24.co</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-6 h-6 text-blue-600" />
                <span className="text-gray-700">+1 (418) 474-5441</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-6 h-6 text-blue-600" />
                <span className="text-gray-700">Kampala, Uganda</span>
              </div>
            </div>
          </div>
        )}
      </Motion>

      {/* Form / Success Message Section */}
      <Motion
        defaultStyle={{ opacity: 0, translateY: 20 }}
        style={{
          opacity: spring(1, { stiffness: 60, damping: 15 }),
          translateY: spring(0, { stiffness: 60, damping: 15 }),
        }}
      >
        {(style) => (
          <div
            className="p-4 mx-auto max-w-xl "
            style={{ opacity: style.opacity, transform: `translateY(${style.translateY}px)` }}
          >
            {isSubmitted ? (
              <div className="text-center">
                <Lottie animationData={checkAnimation} loop={false} className="w-32 h-32 mx-auto" />
                <h2 className="text-2xl font-bold text-gray-800 mt-4">Message Sent!</h2>
                <p className="mt-2 text-gray-600">
                  We’ve received your message and will get back to you within{' '}
                  <strong>1 to 3 working days</strong>. Thank you for reaching out!
                </p>
              </div>
            ) : (
              <>
                <h2 className="text-2xl text-gray-800 font-bold text-center mb-4">
                  Send us a message
                </h2>
                <Form
                  id="contact-form"
                  onSubmit={async (values) => {
                    console.log(values);
                    try {
                      await contactMutation.mutateAsync({ data: values });
                    } catch (error) {
                      console.error(error);
                    }
                  }}
                >
                  {({ register }) => (
                    <>
                      <InputField
                        name="name"
                        placeholder="Your Name"
                        required
                        className="w-full py-2.5 px-4 text-gray-800 bg-gray-100 border focus:border-black focus:bg-transparent text-sm outline-none transition-all"
                        registration={register('name', { required: true })}
                      />
                      <InputField
                        name="email"
                        type="email"
                        placeholder="Your Email"
                        required
                        className="w-full py-2.5 px-4 text-gray-800 bg-gray-100 border focus:border-black focus:bg-transparent text-sm outline-none transition-all"
                        registration={register('email', { required: true })}
                      />
                      <RegularTextAreaField
                        name="message"
                        placeholder="Your Message"
                        required
                        rows={4}
                        className="w-full px-4 text-gray-800 bg-gray-100 border focus:border-black focus:bg-transparent text-sm pt-3 outline-none transition-all"
                        registration={register('message', { required: true })}
                      />
                      <Button
                        type="submit"
                        className="w-full bg-black hover:bg-gray-900 text-white tracking-wide text-sm py-2.5"
                      >
                        {contactMutation.isLoading ? 'Sending...' : 'Send'}
                      </Button>
                    </>
                  )}
                </Form>
              </>
            )}
          </div>
        )}
      </Motion>
    </WebLayout>
  );
};
